/**
 * PhpReplicaPage — 100% native React replacement of every PHP EasyAdmin
 * page. Reads from `php_*` Postgres mirror tables, writes to the new Node
 * CRUD endpoints. **No iframe, no proxy, no PHP runtime dependency.**
 *
 *   Read:    GET    /api/admin/php-mirror/:table/rows
 *            GET    /api/admin/php-mirror/:table/row/:id
 *   Create:  POST   /api/admin/php-mirror/:table/rows
 *   Update:  PATCH  /api/admin/php-mirror/:table/row/:id
 *   Toggle:  PATCH  /api/admin/php-mirror/:table/row/:id/field
 *   Delete:  DELETE /api/admin/php-mirror/:table/row/:id
 *   Export:  GET    /api/admin/php-mirror/:table/export.csv
 *
 * The visual structure (columns, filters, row buttons, add/edit form
 * fields, tabs) is driven by `php-page-specs.generated.json`. The
 * crawler captures everything from the legacy PHP controllers; this
 * component just renders it on Ant Design.
 */
import { useMemo, useState } from 'react';
import {
  Alert, Card, DatePicker, Drawer, Empty, Input, Modal, Select, Space, Table, Tag,
  Tooltip, Typography, Button, Switch, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { ApiError, api } from '../../api/client';
import specsJson from '../../config/php-page-specs.generated.json';
import {
  PhpNativeForm,
  confirmAndDelete,
  type FormFieldSpec,
  type FormSpec,
} from './PhpNativeForm';
import { applyOverlay } from './php-page-spec-overlays';

/* --------------------------------- types --------------------------------- */

interface OperatBtn {
  text: string;
  url_key?: string;
  url?: string;
  method?: string;
  auth?: string;
  cls?: string;
  icon?: string;
  tip?: string;
}

interface ColumnSpec {
  field?: string;
  title: string;
  width?: number;
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  sort?: boolean;
  search?: 'text' | 'select' | 'range' | false;
  selectList?: Record<string, string> | null;
  fieldAlias?: string;
  edit?: 'text' | false;
  templet?: { kind: 'image' | 'nested' | 'tag' | 'raw' | 'switch' | 'icon' | 'tool' | 'badge'; path?: string };
  hide?: boolean;
  operat?: OperatBtn[];
}

interface ToolbarBtn {
  text: string;
  url?: string;
  url_key?: string;
  method?: string;
  auth?: string;
  icon?: string;
  cls?: string;
}

interface PageSpec {
  route: string;
  label_zh: string;
  php_href: string;
  controller_js: string;
  table_name: string | null;
  module_slug?: string | null;
  kind?: 'list' | 'form' | 'unknown';
  init: Record<string, string>;
  toolbar: { refresh: boolean; buttons: ToolbarBtn[] };
  cols: ColumnSpec[];
  form?: FormSpec | null;
  add_form?: FormSpec | null;
  edit_form?: FormSpec | null;
  action_forms?: Record<string, FormSpec> | null;
  warnings: string[];
  /**
   * Overlay-injected: list of `init` URL keys that the PHP backend treats
   * as POST-only "fire and forget" actions (no form fields). The renderer
   * shows a confirm modal instead of opening a form drawer.
   */
  _instant_action_keys?: string[];
}

interface SpecsFile { _generated_at: string; pages: PageSpec[] }

const SPECS = (specsJson as unknown as SpecsFile).pages;
const findSpec = (path: string): PageSpec | null => {
  const base = SPECS.find((s) => s.route === path);
  if (!base) return null;
  return applyOverlay(base as unknown as Parameters<typeof applyOverlay>[0], SPECS as unknown as Parameters<typeof applyOverlay>[1]) as unknown as PageSpec;
};

interface RowsResponse {
  table: string;
  total: number;
  page: number;
  page_size: number;
  columns: string[];
  rows: Record<string, unknown>[];
  /** Backend: no physical `php_*` table on this Postgres (wrong DB or mirror never created it). */
  mirror_table_missing?: boolean;
}

interface RowDetail { table: string; id: string; _raw: unknown; row: Record<string, unknown> }

interface DbTargetDiag {
  fingerprint: string;
  current_database: string;
}

interface PhpMirrorTablesPayload {
  tables: unknown[];
  db_target?: DbTargetDiag;
}

/* ----------------------- synthesize / enrich form spec ------------------- */

/**
 * Build a form spec from the list-page columns when the PHP add/edit page
 * cannot be crawled (e.g. it returns 500). Lets us still edit every row
 * via the Node CRUD endpoints — coverage > zero coverage.
 */
function synthesizeFormFromCols(cols: ColumnSpec[]): FormSpec | null {
  const fields: FormFieldSpec[] = [];
  for (const c of cols) {
    if (!c.field) continue;
    if (c.field.includes('.')) continue;        // nested / virtual
    if (c.templet?.kind === 'tool') continue;   // operat column
    if (c.field === 'id') continue;             // primary key, never edit
    let type: string = 'text';
    if (c.templet?.kind === 'switch') type = 'switch';
    else if (c.templet?.kind === 'image') type = 'image';
    else if (c.selectList) type = 'select';
    fields.push({
      name: c.field,
      label: c.title,
      type,
      required: false,
      options: c.selectList
        ? Object.entries(c.selectList).map(([value, label]) => ({ value, label }))
        : undefined,
    });
  }
  if (!fields.length) return null;
  return { submit_url: null, tabs: [{ title: '字段（自动生成）', fields }] };
}

/* ----------------------- enrich form spec with options ------------------- */

/** Merge select options from `cols.selectList` into the matching form fields. */
function enrichForm(form: FormSpec | null | undefined, cols: ColumnSpec[]): FormSpec | null {
  if (!form) return null;
  const optionsByField = new Map<string, { value: string; label: string }[]>();
  for (const c of cols) {
    if (c.field && c.selectList) {
      optionsByField.set(
        c.field,
        Object.entries(c.selectList).map(([value, label]) => ({ value, label })),
      );
    }
  }
  return {
    ...form,
    tabs: form.tabs.map((t) => ({
      ...t,
      fields: t.fields.map<FormFieldSpec>((f) => {
        if (f.options) return f;
        const opts = optionsByField.get(f.name);
        if (!opts) return f;
        // Promote text→select when we know the option list, but only if the
        // PHP form didn't already declare a richer widget.
        const promotedType = ['text', 'input'].includes(f.type) ? 'select' : f.type;
        return { ...f, type: promotedType, options: opts };
      }),
    })),
  };
}

/* ----------------------------- value rendering --------------------------- */

function tryParseJson(s: unknown): unknown {
  if (typeof s !== 'string') return s;
  const t = s.trim();
  if (!t || (!t.startsWith('{') && !t.startsWith('['))) return s;
  try { return JSON.parse(t); } catch { return s; }
}

function readPath(obj: unknown, path: string): unknown {
  let cur: unknown = obj;
  for (const seg of path.split('.')) {
    if (cur === null || cur === undefined) return undefined;
    if (typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

/**
 * PHP/Eloquent uses camelCase relation names like `userLevel`, `rewardLevel`,
 * `donationLevel`. The mirrored Postgres rows expose the underlying snake_case
 * column (`user_level`, `reward_level`, …) which usually contains the
 * relation's serialized JSON. Map the head segment so dotted-field renderers
 * resolve against the actual storage column.
 */
function camelToSnake(s: string): string {
  return s.replace(/[A-Z]/g, (m, i) => (i === 0 ? m.toLowerCase() : `_${m.toLowerCase()}`));
}

function readRowField(row: Record<string, unknown>, head: string): unknown {
  if (head in row) return row[head];
  const snake = camelToSnake(head);
  if (snake !== head && snake in row) return row[snake];
  return undefined;
}

function fmtCell(v: unknown, max = 200): string {
  if (v === null || v === undefined || v === '') return '-';
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  const s = String(v);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

interface RenderCtx {
  table: string;
  /** Refetch the list after a write */
  refetch: () => void;
}

function renderCell(spec: ColumnSpec, row: Record<string, unknown>, ctx: RenderCtx): React.ReactNode {
  const nestedPath = spec.templet?.kind === 'nested' && spec.templet.path
    ? spec.templet.path
    : (spec.field && spec.field.includes('.') ? spec.field : null);
  if (nestedPath) {
    const [head, ...rest] = nestedPath.split('.');
    const raw = readRowField(row, head);
    if (raw === null || raw === undefined || raw === '' || raw === '0') return '-';
    const parsed = tryParseJson(raw);
    const v = rest.length === 0 ? parsed : readPath(parsed, rest.join('.'));
    return v === undefined || v === null || v === '' ? '-' : fmtCell(v, 80);
  }

  if (spec.templet?.kind === 'image') {
    const v = spec.field ? row[spec.field] : undefined;
    if (!v || typeof v !== 'string') return '-';
    return <img src={v} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />;
  }

  if (spec.templet?.kind === 'switch' && spec.field) {
    const v = row[spec.field];
    const checked = String(v) === '1' || v === true;
    const id = String((row as { id?: unknown }).id ?? '');
    return (
      <Space size="small">
        <Switch
          size="small"
          checked={checked}
          onChange={async (next) => {
            try {
              await api.patch(
                `/api/admin/php-mirror/${encodeURIComponent(ctx.table)}/row/${encodeURIComponent(id)}/field`,
                { field: spec.field, value: next ? '1' : '0' },
              );
              message.success('已更新');
              ctx.refetch();
            } catch (e) {
              message.error(e instanceof Error ? e.message : String(e));
            }
          }}
        />
      </Space>
    );
  }

  if (spec.templet?.kind === 'icon') {
    const v = spec.field ? row[spec.field] : undefined;
    if (typeof v !== 'string' || !v) return '-';
    return <i className={v} style={{ fontSize: 16 }} />;
  }

  if (spec.field && spec.selectList) {
    const v = row[spec.field];
    const key = v === null || v === undefined ? '' : String(v);
    const label = spec.selectList[key];
    if (label !== undefined) return <Tag>{label}</Tag>;
    const parsed = tryParseJson(v);
    if (parsed && typeof parsed === 'object') {
      const t = (parsed as { title?: string }).title;
      if (t) return <Tag>{t}</Tag>;
    }
    return fmtCell(v, 60);
  }

  if (spec.field && /level/i.test(spec.field)) {
    const v = row[spec.field];
    const parsed = tryParseJson(v);
    if (parsed && typeof parsed === 'object') {
      const t = (parsed as { title?: string }).title;
      if (t) return <Tag>{t}</Tag>;
    }
    return fmtCell(v);
  }

  if (spec.field) return fmtCell(row[spec.field]);
  return '-';
}

/* ----------------------------- main component ----------------------------- */

interface PendingEdit {
  /** undefined = create new */
  rowId?: string;
  initialValues?: Record<string, unknown>;
  title: string;
  /** Which form spec to render. */
  formKind: 'add' | 'edit' | 'action';
  /** When formKind === 'action', the init key (e.g. 'password_url'). */
  actionKey?: string;
}

export function PhpReplicaPage({ pathname }: { pathname: string }) {
  const spec = useMemo(() => findSpec(pathname), [pathname]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [drawerRow, setDrawerRow] = useState<RowDetail | null>(null);
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null);

  const enrichedAddForm = useMemo(
    () => enrichForm(spec?.add_form, spec?.cols ?? []),
    [spec?.add_form, spec?.cols],
  );
  const enrichedEditForm = useMemo(
    () => enrichForm(spec?.edit_form, spec?.cols ?? []),
    [spec?.edit_form, spec?.cols],
  );
  const enrichedConfigForm = useMemo(
    () => enrichForm(spec?.form, spec?.cols ?? []),
    [spec?.form, spec?.cols],
  );
  const enrichedActionForms = useMemo(() => {
    const out: Record<string, FormSpec> = {};
    for (const [k, v] of Object.entries(spec?.action_forms ?? {})) {
      const enriched = enrichForm(v, spec?.cols ?? []);
      if (enriched) out[k] = enriched;
    }
    return out;
  }, [spec?.action_forms, spec?.cols]);

  /** Last-resort form built directly from column metadata. */
  const synthesizedForm = useMemo(
    () => synthesizeFormFromCols(spec?.cols ?? []),
    [spec?.cols],
  );

  /** Resolve which spec to use for the open drawer. */
  function formForPending(p: PendingEdit | null): FormSpec | null {
    if (!p) return null;
    if (p.formKind === 'action' && p.actionKey) {
      return enrichedActionForms[p.actionKey] ?? enrichedEditForm ?? enrichedAddForm ?? synthesizedForm ?? null;
    }
    if (p.formKind === 'edit') {
      return enrichedEditForm ?? enrichedAddForm ?? synthesizedForm ?? null;
    }
    return enrichedAddForm ?? synthesizedForm ?? null;
  }

  const q = useMemo(() => {
    const parts = Object.values(filterValues).filter(Boolean);
    return parts.join(' ');
  }, [filterValues]);

  const mirrorListEnabled =
    !!spec?.table_name && (spec?.cols?.length ?? 0) > 0 && spec?.kind !== 'form';

  /** Fingerprint ships with GET /php-mirror/tables (same deploy as /rows); avoids 404 on older /diagnostics paths. */
  const dbTargetQ = useQuery<DbTargetDiag | null>({
    queryKey: ['php-mirror', 'tables', 'db-target'],
    queryFn: async () => {
      const data = await api.get<PhpMirrorTablesPayload>('/api/admin/php-mirror/tables');
      return data.db_target ?? null;
    },
    staleTime: 60_000,
    enabled: mirrorListEnabled,
  });

  const rowsQ = useQuery<RowsResponse>({
    queryKey: ['php-mirror', spec?.table_name, page, pageSize, q],
    queryFn: () =>
      api.get<RowsResponse>(
        `/api/admin/php-mirror/${encodeURIComponent(spec!.table_name!)}/rows`,
        { page, page_size: pageSize, q: q || undefined, sort: 'id_desc' },
        // Large php_* tables: COUNT(*) + first page can exceed default 30s axios timeout.
        { timeout: 120_000 },
      ),
    enabled: mirrorListEnabled,
    staleTime: 0,
  });

  if (!spec) {
    return (
      <Alert
        type="warning"
        showIcon
        message={`未找到该 PHP 镜像页规格：${pathname}`}
        description="重新执行 pnpm --filter @uo/backend php:crawl-page-specs 即可同步。"
      />
    );
  }

  /* ---------- kind:'form' / dashboard pages → render native form ---------- */
  if (spec.kind === 'form' || (spec.cols.length === 0 && enrichedConfigForm)) {
    if (!enrichedConfigForm) {
      return (
        <Card title={spec.label_zh}>
          <Alert type="warning" showIcon message="未提取到表单字段。" description={spec.warnings.join('; ')} />
        </Card>
      );
    }
    // Single-row "config" pages: load the most recent row as initialValues.
    return (
      <Card size="small" title={<Space><span>{spec.label_zh}</span>{spec.table_name && <Tag>{spec.table_name}</Tag>}</Space>}>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          配置表单 — 写入直接作用于 <code>{spec.table_name}</code>。
        </Typography.Paragraph>
        {spec.table_name && (
          <PhpNativeForm
            table={spec.table_name}
            form={enrichedConfigForm}
          />
        )}
      </Card>
    );
  }

  if (!spec.table_name) {
    return (
      <Card title={spec.label_zh}>
        <Alert type="info" showIcon message="该 PHP 页面没有对应的镜像表（可能是表单或仪表盘页）。" />
        <Typography.Paragraph type="secondary">旧路径：<code>{spec.php_href}</code></Typography.Paragraph>
      </Card>
    );
  }

  if (spec.cols.length === 0) {
    return (
      <Card title={spec.label_zh}>
        <Alert
          type="warning"
          showIcon
          message="该页未提取到列定义（PHP 控制器使用了非标准模板）。"
          description={spec.warnings.join('; ')}
        />
        <Typography.Paragraph type="secondary">
          原始 JS 控制器：<code>/static/{spec.controller_js}</code>
        </Typography.Paragraph>
      </Card>
    );
  }

  const tableName = spec.table_name;
  const ctx: RenderCtx = { table: tableName, refetch: () => rowsQ.refetch() };

  /* ---------- list page table columns ---------- */
  const isOperatCol = (c: ColumnSpec) =>
    c.templet?.kind === 'tool' || (c.operat && c.operat.length > 0);

  // When PHP exposes edit/delete URLs in `init` but the controller wires the
  // row buttons via JS (so the crawler captures no `templet:'tool'` column),
  // synthesize an 操作 column so the user can still edit/delete every row.
  const baseCols = spec.cols.filter((c) => !c.hide);
  const hasOperat = baseCols.some(isOperatCol);
  const init = spec.init ?? {};
  const synthesizedOps: OperatBtn[] = [];
  if (!hasOperat) {
    if (init.edit_url) synthesizedOps.push({ text: '编辑', url_key: 'edit_url' });
    // Custom-action buttons that the PHP controller wired through `init.*_url`
    // but never emitted as a `tool` column. Skip the obviously-non-row ones.
    const SKIP_KEYS = new Set([
      'index_url', 'add_url', 'edit_url', 'delete_url', 'export_url',
      'data_url', 'table_render_id', 'table_elem',
    ]);
    for (const [k, v] of Object.entries(init)) {
      if (!k.endsWith('_url') || SKIP_KEYS.has(k) || !v) continue;
      const form = enrichedActionForms[k];
      if (!form) continue; // only auto-add buttons backed by a real action form
      synthesizedOps.push({ text: k.replace(/_url$/, '').replace(/_/g, ' '), url_key: k });
    }
    if (init.delete_url) synthesizedOps.push({ text: '删除', url_key: 'delete_url' });
  }

  const visibleCols: ColumnSpec[] = synthesizedOps.length > 0
    ? [
        ...baseCols,
        {
          title: '操作',
          width: 80 + synthesizedOps.length * 80,
          templet: { kind: 'tool' },
          operat: synthesizedOps,
        } as ColumnSpec,
      ]
    : baseCols;

  // Ant Table breaks if `fixed: 'right'` is set on cols interleaved with non-fixed cols
  // (the unpinned cols collapse to width 0, which is exactly the user.UserOperation
  // bug we hit). So only pin the *trailing contiguous block* of operat columns —
  // any earlier operat columns render inline alongside their data columns, matching
  // the original PHP/LayUI layout.
  let pinFromIdx = visibleCols.length;
  for (let i = visibleCols.length - 1; i >= 0; i--) {
    if (isOperatCol(visibleCols[i])) pinFromIdx = i;
    else break;
  }
  const columns: ColumnsType<Record<string, unknown>> = visibleCols.map((c, i) => {
    const key = c.field ?? `col_${i}`;
    const dotted = c.field?.includes('.');
    const isOperat = isOperatCol(c);
    return {
      title: c.title,
      key,
      ...(dotted || isOperat ? {} : { dataIndex: c.field }),
      width: c.width,
      align: c.align,
      sorter: c.sort && !dotted && !isOperat
        ? (a, b) => Number(a[c.field!] ?? 0) - Number(b[c.field!] ?? 0)
        : undefined,
      ellipsis: !isOperat,
      fixed: isOperat && i >= pinFromIdx ? ('right' as const) : undefined,
      render: (_v: unknown, row: Record<string, unknown>) => {
        if (!isOperat) return renderCell(c, row, ctx);
        const rowId = String((row as { id?: unknown }).id ?? '');
        const buttons = c.operat ?? [];
        if (buttons.length === 0) return <Tag color="default">-</Tag>;
        return (
          <Space size={4} wrap>
            {buttons.map((b, bi) => {
              const cls = b.cls ?? '';
              // PHP/LayUI styles the row buttons via class names. Map them to
              // Ant Design colour tokens so the parity page matches the
              // original UI (e.g. layui-btn-success → green 重置密码 buttons).
              const isDelete = /layui-btn-danger/.test(cls)
                || b.url_key === 'delete_url'
                || /^删除$/.test(b.text);
              const isSuccess = /layui-btn-success/.test(cls);
              const isWarm = /layui-btn-warm/.test(cls);
              const isPlain = /layui-btn-primary/.test(cls);
              // Match ONLY the canonical edit button ("编辑" / `edit_url`).
              // edit1_url..edit5_url are custom actions (重置密码 / 冻结 / 限额 / 红心 …)
              // and must NOT route to the generic edit drawer — they have
              // their own action_forms or instant-action handlers.
              //
              // Some controllers (e.g. user.UserOperation) ALSO repurpose
              // the canonical `edit_url` as an instant action (重置登录密码).
              // If the spec already classifies this key as instant or as an
              // action form, treat it as a custom action — never the
              // generic edit drawer.
              const instantKeys = spec._instant_action_keys ?? [];
              const isCustomAction = !!b.url_key && (
                instantKeys.includes(b.url_key)
                || !!enrichedActionForms[b.url_key]
              );
              const isEdit = !isCustomAction
                && (b.url_key === 'edit_url' || /^编辑$/.test(b.text));
              const buttonProps: {
                color?: 'green' | 'orange' | 'red' | 'blue';
                variant?: 'solid' | 'outlined';
                type?: 'primary' | 'default';
                danger?: boolean;
              } = isDelete
                ? { danger: true }
                : isSuccess
                  ? { color: 'green', variant: 'solid' }
                  : isWarm
                    ? { color: 'orange', variant: 'solid' }
                    : isPlain
                      ? { color: 'blue', variant: 'outlined' }
                      : { type: 'primary' };
              return (
                <Button
                  key={bi}
                  size="small"
                  {...buttonProps}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    if (isDelete) {
                      confirmAndDelete(tableName, rowId, () => rowsQ.refetch());
                      return;
                    }
                    if (isEdit) {
                      api
                        .get<RowDetail>(`/api/admin/php-mirror/${encodeURIComponent(tableName)}/row/${encodeURIComponent(rowId)}`)
                        .then((d) => setPendingEdit({
                          rowId,
                          initialValues: d.row,
                          title: `${spec.label_zh} · 编辑 · id=${rowId}`,
                          formKind: 'edit',
                        }))
                        .catch((e) => message.error(e instanceof Error ? e.message : String(e)));
                      return;
                    }
                    // Custom action button (重置密码 / 冻结 / 限额 / 红心 / …).
                    // If the spec marks it as an "instant action" (no form
                    // fields), confirm-and-POST straight to the PHP backend
                    // via /php-action, then refetch the local mirror.
                    const actionKey = b.url_key;
                    const urlPath = actionKey ? init[actionKey] : null;
                    if (!actionKey || !urlPath) return;

                    const isInstant = (spec._instant_action_keys ?? []).includes(actionKey);
                    if (isInstant) {
                      Modal.confirm({
                        title: `${b.text} · id=${rowId}`,
                        content: `确认对该行执行「${b.text}」操作？该动作直接作用于线上数据库。`,
                        okText: '执行',
                        okButtonProps: { danger: /reset|delete|password/i.test(actionKey) },
                        onOk: async () => {
                          try {
                            const r = await api.post<{ php: { msg: string }; refreshed: boolean }>(
                              `/api/admin/php-mirror/${encodeURIComponent(tableName)}/row/${encodeURIComponent(rowId)}/php-action`,
                              { url_path: urlPath, payload: {} },
                            );
                            message.success(r.php?.msg || '操作成功');
                            rowsQ.refetch();
                          } catch (e) {
                            message.error(e instanceof Error ? e.message : String(e));
                          }
                        },
                      });
                      return;
                    }

                    const hasActionForm = !!enrichedActionForms[actionKey];
                    api
                      .get<RowDetail>(`/api/admin/php-mirror/${encodeURIComponent(tableName)}/row/${encodeURIComponent(rowId)}`)
                      .then((d) => setPendingEdit({
                        rowId,
                        initialValues: d.row,
                        title: `${spec.label_zh} · ${b.text} · id=${rowId}`,
                        formKind: hasActionForm ? 'action' : 'edit',
                        actionKey: hasActionForm ? actionKey : undefined,
                      }))
                      .catch((e) => message.error(e instanceof Error ? e.message : String(e)));
                  }}
                >
                  {b.text}
                </Button>
              );
            })}
          </Space>
        );
      },
    };
  });

  /* ---------- search/filter strip ---------- */
  const filterCols = visibleCols.filter((c) => c.search && c.field);

  const searchStrip = filterCols.length > 0 && (
    <Space wrap style={{ marginBottom: 12 }}>
      {filterCols.map((c) => {
        const k = c.fieldAlias ?? c.field!;
        if (c.search === 'select' && c.selectList) {
          return (
            <Select
              key={k}
              allowClear
              placeholder={c.title}
              style={{ minWidth: 180 }}
              value={filterValues[k] || undefined}
              onChange={(v) => { setFilterValues((s) => ({ ...s, [k]: v ?? '' })); setPage(1); }}
              options={Object.entries(c.selectList).map(([, label]) => ({ value: label, label: `${label}` }))}
            />
          );
        }
        if (c.search === 'range') {
          return (
            <DatePicker.RangePicker
              key={k}
              placeholder={[`${c.title}起`, `${c.title}止`]}
              onChange={(dates) => {
                const v = dates && dates[0] && dates[1]
                  ? `${dayjs(dates[0]).format('YYYY-MM-DD')} ${dayjs(dates[1]).format('YYYY-MM-DD')}`
                  : '';
                setFilterValues((s) => ({ ...s, [k]: v }));
                setPage(1);
              }}
            />
          );
        }
        return (
          <Input
            key={k}
            allowClear
            placeholder={c.title}
            style={{ width: 180 }}
            value={filterValues[k] || ''}
            onChange={(e) => setFilterValues((s) => ({ ...s, [k]: e.target.value }))}
            onPressEnter={() => setPage(1)}
          />
        );
      })}
      {Object.values(filterValues).some(Boolean) && (
        <Button onClick={() => setFilterValues({})}>重置</Button>
      )}
    </Space>
  );

  /* ---------- toolbar ---------- */
  // Show Add when PHP itself exposes add_url (even if we had to fall back to
  // a synthesized form because the legacy add page returned 500).
  const hasAdd = !!(spec.init?.add_url) && !!(enrichedAddForm || synthesizedForm);
  const exportUrl = `/api/admin/php-mirror/${encodeURIComponent(tableName)}/export.csv${q ? `?q=${encodeURIComponent(q)}` : ''}`;

  return (
    <Card
      size="small"
      title={
        <Space>
          <span>{spec.label_zh}</span>
          <Tag>{tableName}</Tag>
        </Space>
      }
      extra={
        <Space size="small">
          <Tooltip title="重新拉取">
            <Button size="small" type="text" icon={<ReloadOutlined />} onClick={() => rowsQ.refetch()} />
          </Tooltip>
          {hasAdd && (
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setPendingEdit({ title: `${spec.label_zh} · 新增`, formKind: 'add' })}
            >
              新增
            </Button>
          )}
          <Button size="small" icon={<DownloadOutlined />} href={exportUrl} target="_blank">
            导出 CSV
          </Button>
          {spec.toolbar.buttons.map((b, i) => {
            const isDelete = /delete/i.test(b.url_key ?? '');
            return (
              <Tooltip key={i} title={`${b.text}`}>
                <Button
                  size="small"
                  danger={isDelete}
                  disabled={isDelete}
                  onClick={() => message.info(`${b.text}：请使用列表中的对应按钮`)}
                >
                  {b.text}
                </Button>
              </Tooltip>
            );
          })}
        </Space>
      }
    >
      {rowsQ.isError && (
        <Alert
          type="error"
          showIcon
          closable
          style={{ marginBottom: 12 }}
          message="镜像列表接口失败"
          description={
            rowsQ.error instanceof ApiError
              ? `${rowsQ.error.message}（code=${rowsQ.error.code}）`
              : rowsQ.error instanceof Error
                ? rowsQ.error.message
                : String(rowsQ.error)
          }
        />
      )}
      {!rowsQ.isError &&
        rowsQ.data &&
        rowsQ.data.total === 0 &&
        !rowsQ.isFetching && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={
              rowsQ.data.mirror_table_missing
                ? '当前库不存在该镜像表 — 请在本机对「与线上一致指纹」的库运行 php:mirror:prod'
                : '当前后端返回 0 行 — 可能是空表，或镜像写入了别的数据库 / 分页统计未更新'
            }
            description={
              rowsQ.data.mirror_table_missing ? (
                <>
                  <p style={{ marginBottom: 8 }}>
                    接口未在 <code>information_schema</code> 中发现物理表 <Typography.Text code>{tableName}</Typography.Text>
                    。这与「表有数据但 total=0」不同：请先确认镜像已在本机写入<strong>与 Vercel backend 相同</strong>的{' '}
                    <Typography.Text code>DATABASE_URL</Typography.Text>（见下方指纹）。
                  </p>
                  {dbTargetQ.isPending ? (
                    <span>正在读取线上库指纹…</span>
                  ) : dbTargetQ.data ? (
                    <p style={{ marginBottom: 0 }}>
                      线上指纹（本机 <Typography.Text code>pnpm php:mirror:prod</Typography.Text> 须一致）：{' '}
                      <Typography.Text code copyable>{dbTargetQ.data.fingerprint}</Typography.Text> ·{' '}
                      <code>current_database</code>={dbTargetQ.data.current_database}
                    </p>
                  ) : null}
                </>
              ) : dbTargetQ.isPending ? (
                <span>正在读取线上库指纹…</span>
              ) : dbTargetQ.isError ? (
                <>
                  无法读取 <code>/api/admin/php-mirror/tables</code>（
                  {dbTargetQ.error instanceof ApiError
                    ? `code=${dbTargetQ.error.code} ${dbTargetQ.error.message}`
                    : String(dbTargetQ.error)}
                  ）。403：为账号授予 <code>system.php_mirror</code>；404：当前请求的 API 主机上仍是旧部署或 URL 配错（检查{' '}
                  <code>VITE_API_BASE_URL</code> 是否指向<strong>正在用的</strong> backend）。
                </>
              ) : dbTargetQ.data ? (
                <>
                  <p style={{ marginBottom: 8 }}>
                    线上 API 所连数据库指纹（本机 <Typography.Text code>pnpm php:mirror:prod</Typography.Text> 须打印<strong>相同</strong>值才写入同一库）：{' '}
                    <Typography.Text code copyable>
                      {dbTargetQ.data.fingerprint}
                    </Typography.Text>{' '}
                    · <code>current_database</code>={dbTargetQ.data.current_database}
                  </p>
                  <p style={{ marginBottom: 0 }}>
                    在本机：用 Vercel backend 的池化 URL 配置{' '}
                    <Typography.Text code>packages/backend/.env.production.mirror</Typography.Text>（或临时使用已对齐的{' '}
                    <Typography.Text code>.env</Typography.Text>），仓库根目录 <Typography.Text code>pnpm php:mirror:prod</Typography.Text>。
                    若指纹已一致仍显示 0 行：该表在线上可能确实无数据；余额类大表需确认未只向 Docker 跑过{' '}
                    <Typography.Text code>php:mirror:all</Typography.Text>。请<strong>重新部署</strong>含最新 backend 后再试列表（修复了{' '}
                    <Typography.Text code>php_mirror_meta.row_count=0</Typography.Text> 误伤总行数、以及{' '}
                    <Typography.Text code>pg_stat</Typography.Text> 未统计时 total 异常）。
                  </p>
                </>
              ) : (
                <>
                  <p style={{ marginBottom: 8 }}>
                    接口已返回表清单，但缺少 <code>db_target</code> 字段 — 请<strong>重新部署</strong>包含最新代码的 backend。
                  </p>
                  <p style={{ marginBottom: 0 }}>
                    仍可执行 <Typography.Text code>pnpm php:mirror:prod</Typography.Text>，把镜像写入与当前后台相同的{' '}
                    <Typography.Text code>DATABASE_URL</Typography.Text>（见{' '}
                    <Typography.Text code>.env.production.mirror.example</Typography.Text>）。
                  </p>
                </>
              )
            }
          />
        )}
      {searchStrip}
      <Table
        size="small"
        loading={rowsQ.isFetching}
        scroll={{ x: 'max-content' }}
        dataSource={rowsQ.data?.rows ?? []}
        rowKey={(r) => String((r as { id?: unknown }).id ?? Math.random())}
        columns={columns}
        onRow={(record) => ({
          onClick: async () => {
            const id = String((record as { id?: unknown }).id ?? '');
            if (!id) return;
            const detail = await api.get<RowDetail>(
              `/api/admin/php-mirror/${encodeURIComponent(tableName)}/row/${encodeURIComponent(id)}`,
            );
            setDrawerRow(detail);
          },
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize,
          total: rowsQ.data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          showTotal: (t) => `共 ${t} 行 · 第 ${page} 页`,
        }}
        locale={{ emptyText: <Empty description={rowsQ.isError ? '加载失败' : '暂无数据'} /> }}
      />

      {/* Row detail drawer (read-only, shows mirror columns + raw JSON) */}
      <Drawer
        open={!!drawerRow}
        onClose={() => setDrawerRow(null)}
        title={drawerRow ? `${spec.label_zh} · id=${drawerRow.id}` : '行详情'}
        width={780}
        extra={
          drawerRow && (
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  setPendingEdit({
                    rowId: drawerRow.id,
                    initialValues: drawerRow.row,
                    title: `${spec.label_zh} · 编辑 · id=${drawerRow.id}`,
                    formKind: 'edit',
                  });
                  setDrawerRow(null);
                }}
              >
                编辑
              </Button>
              <Button
                danger
                onClick={() => confirmAndDelete(tableName, drawerRow.id, () => {
                  setDrawerRow(null);
                  rowsQ.refetch();
                })}
              >
                删除
              </Button>
            </Space>
          )
        }
      >
        {drawerRow && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card size="small" title="字段（与 PHP 对照）">
              <Table
                size="small"
                showHeader={false}
                pagination={false}
                dataSource={visibleCols
                  .filter((c) => c.templet?.kind !== 'tool')
                  .map((c) => ({
                    key: c.field ?? c.title,
                    title: c.title,
                    field: c.field ?? '-',
                    value: <span>{renderCell(c, drawerRow.row, ctx)}</span>,
                  }))}
                columns={[
                  { dataIndex: 'title', width: 180 },
                  { dataIndex: 'field', width: 160, render: (v) => <code style={{ fontSize: 11, color: '#999' }}>{v}</code> },
                  { dataIndex: 'value' },
                ]}
              />
            </Card>
            <Card size="small" title="原始 JSON (_raw)">
              <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 360, overflow: 'auto', margin: 0 }}>
                {JSON.stringify(drawerRow._raw, null, 2)}
              </pre>
            </Card>
          </Space>
        )}
      </Drawer>

      {/* Native Add/Edit/Action drawer (NO iframe) */}
      <Drawer
        open={!!pendingEdit}
        onClose={() => setPendingEdit(null)}
        title={pendingEdit?.title ?? ''}
        width={760}
        destroyOnClose
      >
        {pendingEdit && (() => {
          const formToUse = formForPending(pendingEdit);
          if (!formToUse) {
            return (
              <Alert
                type="warning"
                showIcon
                message="未提取到该操作的表单字段"
                description={`crawler 未抓取到 ${pendingEdit.formKind === 'edit' ? 'edit.html' : pendingEdit.actionKey ?? 'add.html'}。请重新执行 pnpm --filter @uo/backend php:crawl-page-specs`}
              />
            );
          }
          // For "action" forms (重置密码 / 冻结 / 限额 / 红心 / …) we
          // submit through the /php-action proxy so PHP runs the real
          // business logic (and we re-pull the row into the mirror).
          // For plain edit, we keep submitting straight to the mirror PATCH.
          const actionUrl = pendingEdit.formKind === 'action' && pendingEdit.actionKey
            ? spec.init?.[pendingEdit.actionKey]
            : null;
          return (
            <PhpNativeForm
              table={tableName}
              form={formToUse}
              rowId={pendingEdit.rowId}
              initialValues={pendingEdit.initialValues}
              phpAction={actionUrl ? { url_path: actionUrl } : undefined}
              onSaved={() => {
                setPendingEdit(null);
                rowsQ.refetch();
              }}
            />
          );
        })()}
      </Drawer>
    </Card>
  );
}
